import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Loader2, ArrowLeft, FileText, CheckCircle2, NotebookPen,
  Check, X, Sparkles, ArrowRight, Trophy, PartyPopper, Crown, Copy,
  ThumbsUp, ThumbsDown, SkipForward, ExternalLink, Youtube, ShieldCheck,
  Download, FileDown, ListChecks,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { trackAppEvent } from "@/lib/pmf";
import { Navbar } from "@/components/Navbar";
import { PageTransition } from "@/components/PageTransition";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { celebrate, bigCelebrate } from "@/components/celebrate";
import { AskAI } from "@/components/AskAI";
import { PracticeTasks } from "@/components/PracticeTasks";
import { toast } from "sonner";

function QuizBlock({ quiz, onScore, passNeed = 0 }) {
  const [selected, setSelected] = useState({});
  const [checked, setChecked] = useState(false);

  if (!quiz || quiz.length === 0) return null;

  const allAnswered = quiz.every((_, i) => selected[i] !== undefined);
  const score = quiz.reduce((acc, q, i) => acc + (selected[i] === q.answer_index ? 1 : 0), 0);

  const check = () => { setChecked(true); onScore(score); };
  const retry = () => { setSelected({}); setChecked(false); onScore(null); };
  const passed = passNeed > 0 ? score >= passNeed : true;

  return (
    <div className="rounded-2xl border border-border bg-card shadow-sm p-6 md:p-7" data-testid="lesson-quiz">
      <div className="flex items-center gap-3">
        <span className="grid place-items-center h-9 w-9 rounded-xl bg-primary/10 text-primary"><Sparkles className="h-4 w-4" /></span>
        <h3 className="font-heading font-bold text-xl">Check your understanding</h3>
      </div>
      <p className="text-sm text-muted-foreground mt-1.5">
        {quiz.length} quick questions generated for this lesson.
        {passNeed > 0 && <span className="font-semibold text-foreground"> Score {passNeed}/{quiz.length} to complete the lesson.</span>}
      </p>

      <div className="mt-5 space-y-6">
        {quiz.map((q, qi) => (
          <div key={qi} data-testid={`quiz-q-${qi}`}>
            <p className="font-semibold text-foreground">{qi + 1}. {q.question}</p>
            <div className="mt-3 grid gap-2">
              {q.options.map((opt, oi) => {
                const isSel = selected[qi] === oi;
                const isCorrect = oi === q.answer_index;
                let cls = "border-border bg-background hover:border-primary/40 hover:bg-accent/40";
                if (checked) {
                  if (isCorrect) cls = "border-emerald-300 bg-emerald-50 dark:bg-emerald-500/10 dark:border-emerald-500/40";
                  else if (isSel) cls = "border-destructive/50 bg-destructive/10";
                  else cls = "border-border bg-background opacity-60";
                } else if (isSel) cls = "border-primary bg-primary/5 ring-1 ring-primary";
                return (
                  <button
                    key={oi}
                    data-testid={`quiz-q-${qi}-opt-${oi}`}
                    disabled={checked}
                    onClick={() => setSelected((s) => ({ ...s, [qi]: oi }))}
                    className={`flex items-center gap-3 rounded-xl border p-3 text-left text-sm font-medium transition-all ${cls}`}
                  >
                    <span className="grid place-items-center h-5 w-5 shrink-0 rounded-md border border-border bg-card">
                      {checked && isCorrect ? <Check className="h-3.5 w-3.5 text-emerald-600" /> :
                        checked && isSel ? <X className="h-3.5 w-3.5 text-destructive" /> :
                        isSel ? <span className="h-2 w-2 rounded-full bg-primary" /> : null}
                    </span>
                    <span className="flex-1">{opt}</span>
                  </button>
                );
              })}
            </div>
            {checked && q.explanation && (
              <p className="mt-2 text-xs text-muted-foreground"><span className="font-semibold text-foreground">Why:</span> {q.explanation}</p>
            )}
          </div>
        ))}
      </div>

      {!checked ? (
        <Button data-testid="quiz-check" onClick={check} disabled={!allAnswered} className="mt-6 bg-primary text-primary-foreground">Check my answers</Button>
      ) : (
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <div className={`rounded-xl border p-4 flex items-center gap-3 flex-1 min-w-[240px] ${passed ? "border-emerald-200/70 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10" : "border-amber-300/70 bg-amber-50 dark:bg-amber-500/10"}`} data-testid="quiz-result">
            <Trophy className={`h-6 w-6 ${passed ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600"}`} />
            <p className="font-semibold text-foreground">
              You scored {score} / {quiz.length}.{" "}
              {passed
                ? (score === quiz.length ? "Perfect — lesson unlocked! 🎉" : "Passed — you can complete this lesson ✅")
                : `You need ${passNeed}/${quiz.length} to complete this lesson — give it another go!`}
            </p>
          </div>
          <Button variant="outline" data-testid="quiz-retry" onClick={retry}>Try again</Button>
        </div>
      )}
    </div>
  );
}

function Syllabus({ data, onOpen, idPrefix = "syllabus-item" }) {
  const items = data.syllabus || [];
  if (!items.length) return null;
  const done = items.filter((s) => s.completed).length;
  const pct = Math.round((done / items.length) * 100);
  return (
    <div className="rounded-2xl border border-border bg-card shadow-brutal-sm overflow-hidden" data-testid={idPrefix === "syllabus-item" ? "lesson-syllabus" : "lesson-syllabus-mobile"}>
      <div className="p-4 border-b border-border bg-secondary/40">
        <div className="flex items-center gap-2">
          <ListChecks className="h-4 w-4 text-primary" />
          <p className="font-heading font-bold text-sm">Course content</p>
          <span className="ml-auto text-xs font-semibold text-muted-foreground">{done}/{items.length}</span>
        </div>
        <div className="mt-2.5 h-1.5 rounded-full bg-secondary overflow-hidden">
          <div className="h-full rounded-full bg-primary transition-all duration-700" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <div className="max-h-[54vh] overflow-y-auto">
        {items.map((s) => (
          <button
            key={s.video_id}
            data-testid={`${idPrefix}-${s.index}`}
            onClick={() => !s.current && onOpen(s.video_id)}
            className={`w-full flex items-start gap-3 px-4 py-3 text-left border-b border-border/50 last:border-b-0 transition-colors ${
              s.current ? "bg-primary/5 shadow-[inset_2px_0_0_0_hsl(var(--primary))]" : "hover:bg-accent/50"
            }`}
          >
            <span className={`grid place-items-center h-6 w-6 shrink-0 rounded-full text-[11px] font-bold mt-0.5 ${
              s.completed ? "bg-emerald-100 text-emerald-700" : s.current ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"
            }`}>
              {s.completed ? <Check className="h-3.5 w-3.5" /> : s.index}
            </span>
            <span className="min-w-0 flex-1">
              <span className={`block text-sm font-medium leading-snug line-clamp-2 ${s.current ? "text-primary" : "text-foreground"}`}>{s.title}</span>
              {s.duration_label && <span className="block text-[11px] text-muted-foreground mt-0.5">{s.duration_label}</span>}
            </span>
            {s.current && <span className="shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-primary/10 text-primary mt-0.5">NOW</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Lesson() {
  const { videoId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState("");
  const [quizScore, setQuizScore] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [helpful, setHelpful] = useState(null);
  const [skipping, setSkipping] = useState(false);
  const [tab, setTab] = useState("summary");

  const quizLen = data?.quiz?.length || 0;
  const passNeed = quizLen >= 2 ? Math.max(1, Math.round(quizLen * 0.7)) : 0;
  const gated = !!data && !data.completed && passNeed > 0 && (quizScore == null || quizScore < passNeed);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/lesson/${videoId}`);
      setData(data);
      trackAppEvent("opened_lesson", { video_id: videoId });
      setNotes(data.notes || "");
      setQuizScore(data.quiz_score ?? null);
      setHelpful(data.helpful ?? null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [videoId]);

  useEffect(() => { window.scrollTo(0, 0); load(); }, [load]);

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.post(`/lesson/${videoId}/notes`, { notes });
      toast.success("Notes saved");
    } catch (e) {
      toast.error("Could not save notes");
    } finally {
      setSavingNotes(false);
    }
  };

  const voteHelpful = async (val) => {
    setHelpful(val);
    try {
      await api.post(`/lesson/${videoId}/feedback`, { helpful: val });
      toast.success(val ? "Glad it helped! 🙌" : "Thanks — we'll use this to pick better videos.");
    } catch (e) {
      toast.error("Could not save your feedback");
    }
  };

  const skipLesson = async () => {
    setSkipping(true);
    try {
      const { data: res } = await api.post(`/lesson/${videoId}/skip`);
      if (res.next_video_id) {
        toast.message("Skipped — jumping to the next lesson.");
        navigate(`/lesson/${res.next_video_id}`);
      } else {
        navigate("/dashboard");
      }
    } catch (e) {
      toast.error("Could not skip this lesson");
      setSkipping(false);
    }
  };

  const goQuiz = () => {
    setTab("quiz");
    toast.info(`Quick check first: score ${passNeed}/${quizLen} on the quiz to complete this lesson.`);
    document.querySelector('[data-testid="lesson-tabs"]')?.scrollIntoView({ behavior: "smooth" });
  };

  const complete = async () => {
    if (gated) {
      goQuiz();
      return;
    }
    setCompleting(true);
    try {
      const { data: res } = await api.post(`/lesson/${videoId}/complete`, { quiz_score: quizScore, notes });
      if (res.first_time) {
        if (res.milestone === "course_complete") bigCelebrate();
        else celebrate();
      }
      if (res.finished) {
        if (res.context === "tool") {
          toast.success("You finished this tool course! 🎉");
          navigate("/tools");
        } else {
          toast.success("You finished your whole path! 🎉 Legend.");
          navigate("/dashboard");
        }
      } else if (res.next_video_id) {
        toast.success("Lesson complete! On to the next one.");
        navigate(`/lesson/${res.next_video_id}`);
      } else {
        navigate("/dashboard");
      }
    } catch (e) {
      if (e.response?.status === 409) {
        goQuiz();
      } else {
        toast.error(formatApiError(e.response?.data?.detail));
      }
      setCompleting(false);
    }
  };

  const buildMarkdown = () => {
    const v = data.video;
    const out = [
      `# ${v.title}`,
      "",
      `**Module:** ${data.topic_title}`,
      `**Creator:** ${v.channel}${v.duration_label ? ` · ${v.duration_label}` : ""}`,
      `**Watch free on YouTube:** https://www.youtube.com/watch?v=${v.youtube_id}`,
      "",
    ];
    if (data.summary) out.push("## Lesson summary", "", data.summary, "");
    out.push("## My notes", "", notes?.trim() ? notes : "_(no notes yet)_", "");
    if (data.quiz?.length) {
      out.push("## Quiz", "");
      data.quiz.forEach((q, i) => {
        out.push(`${i + 1}. ${q.question}`);
        q.options.forEach((opt, oi) => out.push(`   - ${oi === q.answer_index ? `**${opt} ✓**` : opt}`));
        if (q.explanation) out.push(`   > ${q.explanation}`);
        out.push("");
      });
    }
    out.push("---", "_Exported from AI Pathshaalaa — we curate free YouTube lessons into structured courses. The video stays free on YouTube and belongs to its creator._");
    return out.join("\n");
  };

  const fileSlug = () =>
    (data.video.title || "lesson").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60) || "lesson";

  const downloadMarkdown = () => {
    const blob = new Blob([buildMarkdown()], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${fileSlug()}-notes.md`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Notes downloaded as Markdown");
  };

  const exportPdf = () => {
    const v = data.video;
    const esc = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const nl = (s) => esc(s).replace(/\n/g, "<br/>");
    const summaryHtml = data.summary ? `<h2>Lesson summary</h2><p>${nl(data.summary)}</p>` : "";
    const notesHtml = `<h2>My notes</h2><p>${notes?.trim() ? nl(notes) : "<em>(no notes yet)</em>"}</p>`;
    let quizHtml = "";
    if (data.quiz?.length) {
      quizHtml = "<h2>Quiz</h2><ol>" + data.quiz.map((q) => {
        const opts = q.options.map((o, oi) =>
          `<li${oi === q.answer_index ? ' style="font-weight:700;color:#16a34a"' : ""}>${esc(o)}${oi === q.answer_index ? " &#10003;" : ""}</li>`).join("");
        return `<li><p style="font-weight:600">${esc(q.question)}</p><ul>${opts}</ul>${q.explanation ? `<p style="color:#64748b"><em>${esc(q.explanation)}</em></p>` : ""}</li>`;
      }).join("") + "</ol>";
    }
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(v.title)} — Notes</title>
<style>body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:#0f172a;max-width:720px;margin:40px auto;padding:0 24px;line-height:1.65}h1{font-size:24px;margin-bottom:4px}h2{font-size:17px;margin-top:28px;border-bottom:1px solid #e2e8f0;padding-bottom:6px}.meta{color:#64748b;font-size:13px}a{color:#4f46e5;text-decoration:none}.brand{margin-top:40px;border-top:1px solid #e2e8f0;padding-top:14px;color:#94a3b8;font-size:12px}@media print{body{margin:0}}</style>
</head><body>
<h1>${esc(v.title)}</h1>
<p class="meta">${esc(data.topic_title)} &middot; ${esc(v.channel)}${v.duration_label ? ` &middot; ${esc(v.duration_label)}` : ""}<br/>Watch free on YouTube: <a href="https://www.youtube.com/watch?v=${v.youtube_id}">youtube.com/watch?v=${v.youtube_id}</a></p>
${summaryHtml}${notesHtml}${quizHtml}
<p class="brand">Exported from AI Pathshaalaa — we curate free YouTube lessons into structured courses. The video stays free on YouTube and belongs to its creator.</p>
</body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Allow pop-ups to download the PDF, then try again."); return; }
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!data) return null;

  const v = data.video;
  const more = data.more_videos || [];
  const isTool = data.context === "tool";

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <PageTransition className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-24">
        <div className="flex items-center justify-between gap-3 mb-5">
          <button data-testid="lesson-back" onClick={() => navigate(isTool ? "/tools" : "/dashboard")} className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="h-4 w-4" /> {isTool ? "Back to tools" : "Back to roadmap"}
          </button>
          {data.lesson_index && (
            <span data-testid="lesson-position" className="font-mono text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
              Lesson {data.lesson_index} of {data.lesson_total}
            </span>
          )}
        </div>

        <div className="grid lg:grid-cols-[minmax(0,1fr)_330px] gap-8 items-start">
        <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-primary/10 text-primary">{data.topic_title}</span>
          {data.completed && (
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
              <CheckCircle2 className="h-3.5 w-3.5" /> Completed
            </span>
          )}
        </div>
        <h1 className="mt-3 font-heading font-bold tracking-tight text-2xl md:text-3xl leading-tight" data-testid="lesson-title">{v.title}</h1>
        <p className="mt-1.5 text-muted-foreground">{v.channel} · {v.duration_label}</p>

        {v.quality_reason && (
          <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-border bg-primary/5 px-4 py-3 max-w-2xl">
            <Crown className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            <p className="text-sm text-foreground/85"><span className="font-semibold text-foreground">Why we picked this:</span> {v.quality_reason}</p>
          </div>
        )}

        {/* player — official, unmodified YouTube IFrame player in privacy-enhanced (no-cookie)
            mode; personalized tracking stays disabled (also satisfies Made-for-Kids handling). */}
        <div className="mt-6 aspect-video overflow-hidden rounded-2xl border border-border shadow-md bg-black" data-testid="lesson-player">
          <iframe
            className="w-full h-full"
            src={`https://www.youtube-nocookie.com/embed/${v.youtube_id}?rel=0`}
            title={v.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>

        {/* YouTube attribution + "curation, not videos" label — shown on every embed */}
        <div className="mt-3 rounded-xl border border-border bg-secondary/50 p-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="yt-attribution">
          <div className="flex items-start gap-2 min-w-0 flex-1">
            <Youtube className="h-5 w-5 text-[#FF0000] shrink-0 mt-0.5" />
            <p className="text-xs sm:text-sm text-muted-foreground leading-snug">
              This video is <span className="font-semibold text-foreground">free on YouTube</span>. Created by{" "}
              {v.channel_id ? (
                <a href={`https://www.youtube.com/channel/${v.channel_id}`} target="_blank" rel="noopener noreferrer"
                   className="font-semibold text-foreground hover:text-primary underline underline-offset-2" data-testid="yt-channel-link">
                  {v.channel}
                </a>
              ) : (
                <span className="font-semibold text-foreground">{v.channel}</span>
              )}
              . We curate and structure it into a course — <span className="font-semibold text-foreground">we do not charge for the video.</span>
              {v.made_for_kids && <span className="block mt-1 text-[11px]"><ShieldCheck className="inline h-3 w-3 mr-1" />Marked "Made for Kids" — personalized tracking is disabled.</span>}
              <span className="block mt-0.5 text-[11px]">Source: YouTube. All views, watch time &amp; ad revenue go to the creator.</span>
            </p>
          </div>
          <a href={`https://www.youtube.com/watch?v=${v.youtube_id}`} target="_blank" rel="noopener noreferrer"
             data-testid="watch-on-youtube"
             className="shrink-0 inline-flex items-center justify-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent transition-colors">
            <ExternalLink className="h-3.5 w-3.5" /> Watch on YouTube
          </a>
        </div>

        {/* more explainers for this topic — different teachers & angles */}
        {more.length > 0 && (
          <div className="mt-6" data-testid="more-explainers">
            <div className="flex items-center gap-3 mb-1.5">
              <span className="grid place-items-center h-9 w-9 rounded-xl bg-primary/10 text-primary"><Copy className="h-4 w-4" /></span>
              <h3 className="font-heading font-bold text-base md:text-lg">
                {more.length} more {more.length === 1 ? "explainer" : "explainers"} for this topic
              </h3>
            </div>
            <p className="text-sm text-muted-foreground mb-3 ml-12">
              Different teachers, different angles — watch the ones that click for you.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              {more.map((m, i) => (
                <button
                  key={m.video_id}
                  data-testid={`more-explainer-${i}`}
                  onClick={() => navigate(`/lesson/${m.video_id}`)}
                  className="text-left rounded-xl bg-card border border-border shadow-sm hover:shadow-md hover:border-primary/30 transition-all p-3 flex items-center gap-3"
                >
                  <span className="grid place-items-center h-10 w-10 shrink-0 rounded-xl bg-primary/10 text-primary font-heading font-bold">
                    {i + 2}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-sm leading-tight line-clamp-2">{m.video_title}</p>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">{m.channel}{m.duration_label ? ` · ${m.duration_label}` : ""}</p>
                  </div>
                  <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* lesson workspace — organized like a professional course player */}
        <Tabs value={tab} onValueChange={setTab} className="mt-8" data-testid="lesson-tabs">
          <TabsList className="w-full justify-start overflow-x-auto no-scrollbar h-auto p-1 rounded-xl">
            <TabsTrigger data-testid="tab-summary" value="summary" className="rounded-lg px-4 py-2">Summary</TabsTrigger>
            <TabsTrigger data-testid="tab-quiz" value="quiz" className="rounded-lg px-4 py-2">Quiz{data.quiz?.length ? ` (${data.quiz.length})` : ""}</TabsTrigger>
            <TabsTrigger data-testid="tab-practice" value="practice" className="rounded-lg px-4 py-2">Practice</TabsTrigger>
            <TabsTrigger data-testid="tab-notes" value="notes" className="rounded-lg px-4 py-2">Notes</TabsTrigger>
            <TabsTrigger data-testid="tab-askai" value="askai" className="rounded-lg px-4 py-2">Ask AI</TabsTrigger>
          </TabsList>

          <TabsContent forceMount value="summary" className="mt-4 data-[state=inactive]:hidden">
        <div className="rounded-2xl border border-border bg-card shadow-sm p-6 md:p-7" data-testid="lesson-summary">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center h-9 w-9 rounded-xl bg-primary/10 text-primary"><FileText className="h-4 w-4" /></span>
            <h3 className="font-heading font-bold text-xl">Lesson summary</h3>
          </div>
          {data.summary ? (
            <p className="mt-4 text-foreground/90 leading-relaxed whitespace-pre-line">{data.summary}</p>
          ) : (
            <p className="mt-4 text-muted-foreground">Summary is being prepared. Refresh in a moment.</p>
          )}
        </div>
          </TabsContent>

          <TabsContent forceMount value="quiz" className="mt-4 data-[state=inactive]:hidden">
            <QuizBlock quiz={data.quiz} onScore={setQuizScore} passNeed={passNeed} />
          </TabsContent>

          <TabsContent forceMount value="practice" className="mt-4 data-[state=inactive]:hidden">
            <PracticeTasks videoId={videoId} />
          </TabsContent>

          <TabsContent forceMount value="notes" className="mt-4 data-[state=inactive]:hidden">
        <div className="rounded-2xl border border-border bg-card shadow-sm p-6 md:p-7" data-testid="lesson-notes">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center h-9 w-9 rounded-xl bg-primary/10 text-primary"><NotebookPen className="h-4 w-4" /></span>
            <h3 className="font-heading font-bold text-xl">Your notes</h3>
          </div>
          <Textarea
            data-testid="notes-textarea"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Jot down key takeaways, questions, ideas to try…"
            className="mt-4 min-h-[120px] border border-border rounded-lg resize-y focus-visible:ring-2 focus-visible:ring-primary/40"
          />
          <div className="mt-3 flex flex-wrap gap-2">
            <Button variant="outline" data-testid="save-notes" onClick={saveNotes} disabled={savingNotes}>
              {savingNotes ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save notes"}
            </Button>
            <Button variant="outline" data-testid="export-notes-md" onClick={downloadMarkdown}>
              <Download className="h-4 w-4" /> Download .md
            </Button>
            <Button variant="outline" data-testid="export-notes-pdf" onClick={exportPdf}>
              <FileDown className="h-4 w-4" /> Save as PDF
            </Button>
          </div>
        </div>

          </TabsContent>

          <TabsContent forceMount value="askai" className="mt-4 data-[state=inactive]:hidden">
            <AskAI videoId={videoId} />
          </TabsContent>
        </Tabs>

        {/* was this helpful */}
        <div className="mt-6 rounded-2xl border border-border bg-card shadow-sm p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="lesson-helpful">
          <div className="flex items-center gap-3 flex-1">
            <span className="grid place-items-center h-9 w-9 rounded-xl bg-primary/10 text-primary"><ThumbsUp className="h-4 w-4" /></span>
            <p className="font-heading font-bold text-lg">Was this teacher helpful?</p>
          </div>
          <div className="flex gap-3">
            <button
              data-testid="helpful-yes"
              onClick={() => voteHelpful(true)}
              className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-all ${helpful === true ? "border-emerald-300 bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:border-emerald-500/40 dark:text-emerald-300" : "border-border bg-background hover:bg-accent"}`}
            >
              <ThumbsUp className="h-4 w-4" /> Yes
            </button>
            <button
              data-testid="helpful-no"
              onClick={() => voteHelpful(false)}
              className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-all ${helpful === false ? "border-orange-300 bg-orange-50 text-orange-700 dark:bg-orange-500/15 dark:border-orange-500/40 dark:text-orange-300" : "border-border bg-background hover:bg-accent"}`}
            >
              <ThumbsDown className="h-4 w-4" /> No
            </button>
          </div>
        </div>

        {/* mobile syllabus */}
        <div className="mt-6 lg:hidden">
          <Syllabus data={data} onOpen={(id) => navigate(`/lesson/${id}`)} idPrefix="m-syllabus-item" />
        </div>
        </div>

        {/* course content sidebar (desktop) */}
        <aside className="hidden lg:block sticky top-24">
          <Syllabus data={data} onOpen={(id) => navigate(`/lesson/${id}`)} />
        </aside>
        </div>

        {/* complete */}
        <div className="mt-8 sticky bottom-4">
          <div className="rounded-2xl border border-border bg-card shadow-md p-4 flex flex-wrap items-center justify-end gap-2 sm:gap-3">
            <p className="text-sm text-muted-foreground hidden md:block mr-auto" data-testid="complete-hint">
              {data.completed
                ? "Already completed — mark again to move on."
                : gated
                  ? `Watch, then pass the quiz (${passNeed}/${quizLen}) to complete this lesson.`
                  : "Done watching? Mark it complete."}
            </p>
            <Button variant="outline" size="sm" data-testid="lesson-prev" onClick={() => navigate(`/lesson/${data.prev_video_id}`)} disabled={!data.prev_video_id || skipping || completing}>
              <ArrowLeft className="h-4 w-4" /> Prev
            </Button>
            <Button variant="outline" size="sm" data-testid="skip-lesson" onClick={skipLesson} disabled={skipping || completing}>
              {skipping ? <Loader2 className="h-4 w-4 animate-spin" /> : <><SkipForward className="h-4 w-4" /> Skip</>}
            </Button>
            <Button data-testid="mark-complete" onClick={complete} disabled={completing} className="bg-primary text-primary-foreground">
              {completing ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                <>
                  {data.completed ? <PartyPopper className="h-4 w-4" /> : <Check className="h-4 w-4" />}
                  <span className="hidden sm:inline">{gated ? "Take the quiz to complete" : "Mark complete & next"}</span>
                  <span className="sm:hidden">{gated ? "Take quiz" : "Complete"}</span>
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </div>
      </PageTransition>
    </div>
  );
}
